import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { TableHeaderProps, ColumnDef } from "./types";

function getColumnStyle(column: ColumnDef<any>) {
  const style: any = {};

  if (column.width) {
    style.width = column.width;
  }
  if (column.minWidth) {
    style.minWidth = column.minWidth;
  }
  if (column.maxWidth) {
    style.maxWidth = column.maxWidth;
  }
  if (column.flex) {
    style.flex = column.flex;
  }
  if (!column.width && !column.flex) {
    style.flex = 1;
  }

  return style;
}

function getAlignStyle(align?: "left" | "center" | "right") {
  switch (align) {
    case "center":
      return { justifyContent: "center" as const };
    case "right":
      return { justifyContent: "flex-end" as const };
    default:
      return { justifyContent: "flex-start" as const };
  }
}

export function TableHeader<T>({
  columns,
  sort,
  onSort,
  sortable = true,
  style,
  showActionsColumn,
  minActionSlots,
  selectable,
  allSelected,
  someSelected,
  onSelectAll,
}: TableHeaderProps<T>) {
  const { colors } = useTheme();

  // Calculate min width for actions header based on minActionSlots
  // Each action: 28px (icon 20 + padding 8), gap: 4px, cell padding: 16px
  const actionsHeaderWidth = minActionSlots ? 16 + 28 * minActionSlots + 4 * Math.max(0, minActionSlots - 1) : 80;

  return (
    <View style={[styles.header, { backgroundColor: colors.card, borderBottomColor: colors.border }, style]}>
      {selectable && (
        <Pressable style={styles.selectHeader} onPress={onSelectAll}>
          <MaterialCommunityIcons
            name={allSelected ? "checkbox-marked" : someSelected ? "minus-box" : "checkbox-blank-outline"}
            size={24}
            color={allSelected || someSelected ? colors.primary : colors.text}
          />
        </Pressable>
      )}
      {columns.map((column) => {
        const isSorted = sort?.key === column.key;
        const canSort = sortable && column.sortable !== false;
        const columnStyle = getColumnStyle(column);
        const alignStyle = getAlignStyle(column.align);

        const headerContent = (
          <View style={[styles.headerCell, alignStyle]}>
            <ThemedText
              style={[styles.headerText, column.headerStyle, isSorted && { color: colors.primary }]}
              numberOfLines={1}
            >
              {column.header}
            </ThemedText>
            {canSort && isSorted && (
              <MaterialCommunityIcons
                name={sort.direction === "asc" ? "chevron-up" : "chevron-down"}
                size={16}
                color={colors.primary}
                style={styles.sortIcon}
              />
            )}
          </View>
        );

        if (canSort) {
          return (
            <Pressable
              key={column.key}
              onPress={() => onSort(column.key)}
              style={({ pressed }) => [columnStyle, pressed && { opacity: 0.7 }]}
            >
              {headerContent}
            </Pressable>
          );
        }

        return (
          <View key={column.key} style={columnStyle}>
            {headerContent}
          </View>
        );
      })}
      {showActionsColumn && (
        <View style={[styles.headerCell, styles.actionsHeader, { minWidth: actionsHeaderWidth }]} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    borderBottomWidth: 1,
    minHeight: 40,
  },
  selectHeader: {
    minWidth: 48,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  headerCell: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sortIcon: {
    marginLeft: 4,
  },
  actionsHeader: {
    width: 80,
    justifyContent: "center",
  },
});
